import InputField from 'components/fields/InputField';
import { useEffect, useState } from 'react';
import { RiEyeCloseLine, RiEyeLine } from 'react-icons/ri';

const CreateAccountant = ({
  setAccountantData,
  accountantData,
  setDisable,
}) => {
  const [errors, setErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const capitalizeWords = (str) =>
    str.replace(/\b\w/g, (char) => char.toUpperCase());

  const isValidPhone = (phone) => /^[6-9]\d{9}$/.test(phone); // Indian phone validation
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateField = (field, value) => {
    const errors = {};

    switch (field) {
      case 'accountantName':
        if (!value.trim()) {
          errors.accountantName = 'Accountant name is required.';
        }
        break;
      case 'accountantEmail':
        if (!isValidEmail(value)) {
          errors.accountantEmail = 'Please enter a valid email address.';
        }
        break;
      case 'contactNumber':
        if (!isValidPhone(value)) {
          errors.contactNumber =
            'Please enter a valid phone number (10 digits, starting with 6-9).';
        }
        break;
      case 'accountantPassword':
        const passwordPattern =
          /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,15}$/;
        if (!passwordPattern.test(value)) {
          errors.accountantPassword =
            'Password must be 8-15 characters long, contain at least one uppercase letter, one number, and one special character (!@#$%^&*).';
        }
        break;
      default:
        break;
    }

    setFieldErrors((prevErrors) => ({
      ...prevErrors,
      ...errors,
      [field]: errors[field] || '', // Clear error if validation passes
    }));
  };

  const handleInputChange = (event) => {
    const { id, value } = event.target;

    const formattedValue =
      id === 'accountantName' ? capitalizeWords(value) : value;

    setAccountantData((prevData) => ({
      ...prevData,
      [id]: formattedValue,
    }));

    validateField(id, formattedValue);
  };

  useEffect(() => {
    const newErrors = [];
    const {
      accountantName,
      accountantEmail,
      accountantPassword,
      contactNumber,
    } = accountantData;

    if (
      !accountantName ||
      !accountantEmail ||
      !accountantPassword ||
      !contactNumber.trim()
    ) {
      newErrors.push('Please fill in all fields.');
    }

    if (Object.values(fieldErrors).some((error) => error)) {
      newErrors.push('Please correct the highlighted errors.');
    }

    setErrors(newErrors);
    setDisable(newErrors.length > 0);
  }, [accountantData, fieldErrors, setDisable]);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <div className="h-full w-full rounded-[20px] px-3 pt-7 md:px-8">
      <h4 className="pt-[5px] text-xl font-bold text-navy-700 dark:text-white">
        Accountant Information
      </h4>
      <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <InputField
            extra="mb-3"
            label="Accountant Name"
            placeholder="Enter Accountant Name"
            id="accountantName"
            type="text"
            onChange={handleInputChange}
            value={accountantData?.accountantName}
            maxLength={40}
          />
          {fieldErrors.accountantName && (
            <div className="text-sm text-red-500">
              {fieldErrors.accountantName}
            </div>
          )}
        </div>

        <div>
          <InputField
            extra="mb-3"
            label="Accountant Email"
            placeholder="Enter Accountant Email"
            id="accountantEmail"
            type="email"
            onChange={handleInputChange}
            value={accountantData?.accountantEmail}
            maxLength={40}
          />
          {fieldErrors.accountantEmail && (
            <div className="text-sm text-red-500">
              {fieldErrors.accountantEmail}
            </div>
          )}
        </div>

        <div>
          <InputField
            extra="mb-3"
            label="Accountant Contact Number"
            placeholder="Enter Contact Number"
            id="contactNumber"
            type="text"
            maxLength={10}
            onChange={handleInputChange}
            value={accountantData?.contactNumber}
          />
          {fieldErrors.contactNumber && (
            <div className="text-sm text-red-500">
              {fieldErrors.contactNumber}
            </div>
          )}
        </div>

        <div className="relative mb-3 h-[calc(2.5rem+1rem)]">
          <div>
            <InputField
              extra="mb-3"
              label="Accountant Password"
              placeholder="Create Accountant Password"
              id="accountantPassword"
              type={showPassword ? 'text' : 'password'}
              onChange={handleInputChange}
              value={accountantData?.accountantPassword}
              maxLength={15}
            />
            <button
              type="button"
              className="absolute right-4 top-12 -translate-y-1/2 transform pt-4 text-lg text-navy-700 dark:text-white"
              onClick={togglePasswordVisibility}
            >
              {showPassword ? <RiEyeLine /> : <RiEyeCloseLine />}
            </button>
          </div>
          {fieldErrors.accountantPassword && (
            <div className="text-sm text-red-500">
              {fieldErrors.accountantPassword}
            </div>
          )}
        </div>
      </div>

      {errors.length > 0 && (
        <div className="mt-4 text-red-500">
          {errors.map((error, index) => (
            <p key={index}>{error}</p>
          ))}
        </div>
      )}
    </div>
  );
};

export default CreateAccountant;
